import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';

import { createLocalStorageTourStorage } from './storage';
import type { StartTourOptions, TourCompletionState, TourDefinition, TourStorage, TourStep } from './types';
import { TourOverlay } from './TourOverlay';

type ActiveTourState = {
  definition: TourDefinition;
  stepIndex: number;
};

interface TourContextValue {
  registerTours: (definitions: TourDefinition | TourDefinition[]) => () => void;
  startTour: (tourId: string, options?: StartTourOptions) => boolean;
  nextStep: () => void;
  previousStep: () => void;
  closeTour: (options?: { markComplete?: boolean }) => void;
  skipTour: () => void;
  activeTour: ActiveTourState | null;
  activeStep: TourStep | null;
  completed: TourCompletionState;
  isReady: boolean;
  isTourCompleted: (definition: TourDefinition) => boolean;
  registry: Map<string, TourDefinition>;
  registerAnchor: (anchorId: string, element: HTMLElement | null) => void;
  resolveAnchor: (anchorId?: string) => HTMLElement | null;
  anchorVersion: number;
}

const TourContext = createContext<TourContextValue | null>(null);

export interface TourProviderProps {
  children: ReactNode;
  storage?: TourStorage;
}

export function TourProvider({ children, storage }: TourProviderProps) {
  const resolvedStorage = useMemo(() => storage ?? createLocalStorageTourStorage(), [storage]);
  const [completed, setCompleted] = useState<TourCompletionState>({});
  const [registry, setRegistry] = useState<Map<string, TourDefinition>>(() => new Map());
  const [isReady, setReady] = useState(false);
  const [activeTour, setActiveTour] = useState<ActiveTourState | null>(null);
  const anchorsRef = useRef(new Map<string, HTMLElement>());
  const [anchorVersion, setAnchorVersion] = useState(0);

  useEffect(() => {
    let isMounted = true;
    void resolvedStorage.read().then(result => {
      if (!isMounted) {
        return;
      }
      setCompleted(result ?? {});
      setReady(true);
    });
    return () => {
      isMounted = false;
    };
  }, [resolvedStorage]);

  const writeCompletionState = useCallback(
    (updater: (prev: TourCompletionState) => TourCompletionState) => {
      setCompleted(prev => {
        const next = updater(prev);
        void resolvedStorage.write(next);
        return next;
      });
    },
    [resolvedStorage]
  );

  const registerTours = useCallback((definitions: TourDefinition | TourDefinition[]) => {
    const entries = Array.isArray(definitions) ? definitions : [definitions];
    setRegistry(prev => {
      const next = new Map(prev);
      entries.forEach(def => {
        if (!def?.id) {
          return;
        }
        next.set(def.id, def);
      });
      return next;
    });
    return () => {
      setRegistry(prev => {
        const next = new Map(prev);
        entries.forEach(def => {
          next.delete(def.id);
        });
        return next;
      });
    };
  }, []);

  const isTourCompleted = useCallback(
    (definition: TourDefinition) => {
      const version = completed[definition.id];
      return typeof version === 'number' && version >= definition.version;
    },
    [completed]
  );

  const startTour = useCallback(
    (tourId: string, options?: StartTourOptions) => {
      const definition = registry.get(tourId);
      if (!definition || !definition.steps.length) {
        return false;
      }
      if (!options?.force && isTourCompleted(definition)) {
        return false;
      }
      const targetIndex = options?.stepId
        ? Math.max(
            0,
            definition.steps.findIndex(step => step.id === options.stepId)
          )
        : 0;
      const safeIndex = targetIndex === -1 ? 0 : targetIndex;
      setActiveTour({ definition, stepIndex: safeIndex });
      return true;
    },
    [isTourCompleted, registry]
  );

  const closeTour = useCallback(
    (options?: { markComplete?: boolean }) => {
      setActiveTour(current => {
        if (current && options?.markComplete) {
          writeCompletionState(prev => ({
            ...prev,
            [current.definition.id]: current.definition.version
          }));
        }
        return null;
      });
    },
    [writeCompletionState]
  );

  const nextStep = useCallback(() => {
    setActiveTour(current => {
      if (!current) {
        return current;
      }
      if (current.stepIndex < current.definition.steps.length - 1) {
        return {
          definition: current.definition,
          stepIndex: current.stepIndex + 1
        };
      }
      writeCompletionState(prev => ({
        ...prev,
        [current.definition.id]: current.definition.version
      }));
      return null;
    });
  }, [writeCompletionState]);

  const previousStep = useCallback(() => {
    setActiveTour(current => {
      if (!current) {
        return current;
      }
      if (current.stepIndex === 0) {
        return current;
      }
      return {
        definition: current.definition,
        stepIndex: current.stepIndex - 1
      };
    });
  }, []);

  const skipTour = useCallback(() => {
    setActiveTour(current => {
      if (!current) {
        return current;
      }
      writeCompletionState(prev => ({
        ...prev,
        [current.definition.id]: current.definition.version
      }));
      return null;
    });
  }, [writeCompletionState]);

  const registerAnchor = useCallback((anchorId: string, element: HTMLElement | null) => {
    if (!anchorId) {
      return;
    }
    if (element) {
      anchorsRef.current.set(anchorId, element);
    } else {
      anchorsRef.current.delete(anchorId);
    }
    setAnchorVersion(count => count + 1);
  }, []);

  const resolveAnchor = useCallback((anchorId?: string) => {
    if (!anchorId) {
      return null;
    }
    return anchorsRef.current.get(anchorId) ?? null;
  }, []);

  const activeStep = useMemo(() => {
    if (!activeTour) {
      return null;
    }
    return activeTour.definition.steps[activeTour.stepIndex] ?? null;
  }, [activeTour]);

  const value = useMemo<TourContextValue>(
    () => ({
      registerTours,
      startTour,
      nextStep,
      previousStep,
      closeTour,
      skipTour,
      activeTour,
      activeStep,
      completed,
      isReady,
      isTourCompleted,
      registry,
      registerAnchor,
      resolveAnchor,
      anchorVersion
    }),
    [
      activeStep,
      activeTour,
      anchorVersion,
      closeTour,
      completed,
      isReady,
      isTourCompleted,
      nextStep,
      previousStep,
      registerAnchor,
      registerTours,
      registry,
      resolveAnchor,
      skipTour,
      startTour
    ]
  );

  return (
    <React.Fragment>
      <TourContext.Provider value={value}>
        {children}
        <TourOverlay />
      </TourContext.Provider>
    </React.Fragment>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
